/**
 * AST Operation Applier
 * Applies operations to AST using Immer for immutable updates
 */

import { Draft, produce } from 'immer'

import { AstOperation } from './astOperations'
import { logger } from '../../utils/logger'

import { AstSelection, Block, ContentAST, InlineContent, createParagraph, createTextNode } from './index'

export interface ApplyResult {
  success: boolean
  ast?: ContentAST
  selection?: AstSelection
  editorState?: unknown // Opaque Lexical EditorState for precise undo/redo restoration
  error?: string
  // Undo/redo only: the first press was used to move the cursor to the next
  // change's location (for orientation) and did not pop the stack. The caller
  // should move the cursor but leave content untouched.
  navigationOnly?: boolean
}

/**
 * Applies an operation to an AST immutably
 */
export const applyOperation = (ast: ContentAST, operation: AstOperation): ApplyResult => {
  try {
    const newAst = produce(ast, (draft) => {
      applyOperationToDraft(draft, operation)
    })

    return {
      success: true,
      ast: newAst,
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to apply operation ${operation.type}: ${error}`,
    }
  }
}

/**
 * Applies multiple operations to an AST
 */
export const applyOperations = (ast: ContentAST, operations: AstOperation[]): ApplyResult => {
  try {
    // PERFORMANCE: Fast path for state_change operations (undo/redo)
    // Skip Immer overhead since we're just replacing the entire AST
    if (operations.length === 1 && operations[0].type === 'state_change') {
      const stateOp = operations[0] as any
      if (stateOp.data && stateOp.data.newAST) {
        return {
          success: true,
          ast: stateOp.data.newAST,
        }
      }
    }

    const newAst = produce(ast, (draft) => {
      for (const operation of operations) {
        applyOperationToDraft(draft, operation)
      }
    })

    return {
      success: true,
      ast: newAst,
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to apply operations: ${error}`,
    }
  }
}

/**
 * Applies an operation to a draft AST (used within Immer produce)
 */
const applyOperationToDraft = (draft: Draft<ContentAST>, operation: AstOperation): void => {
  // Handle regular atomic operations
  switch (operation.type) {
    case 'state_change': {
      // Handle state-based operations (replaces entire AST)
      const stateOp = operation as any // Type assertion for StateChangeOperation
      if (stateOp.data && stateOp.data.newAST) {
        const newAST = stateOp.data.newAST
        logger.debug('🔄 Applying state_change operation:')
        logger.debug(
          '🔄 Restoring AST blocks:',
          JSON.stringify(
            newAST.blocks.map((b: any) => ({ id: b.id, type: b.type, content: b.content || b.items || 'no content' })),
            null,
            2,
          ),
        )
        draft.meta = newAST.meta
        draft.blocks = newAST.blocks
      }
      break
    }
    case 'insert_text':
      applyInsertText(draft, operation)
      break

    case 'delete_text':
      applyDeleteText(draft, operation)
      break

    case 'insert_block':
      applyInsertBlock(draft, operation)
      break

    case 'delete_block':
      applyDeleteBlock(draft, operation)
      break

    case 'split_block':
      applySplitBlock(draft, operation)
      break

    case 'merge_blocks':
      applyMergeBlocks(draft, operation)
      break

    case 'set_mark':
      applySetMark(draft, operation)
      break

    case 'remove_mark':
      applyRemoveMark(draft, operation)
      break

    case 'set_block_attrs':
      applySetBlockAttrs(draft, operation)
      break

    case 'move_block':
      applyMoveBlock(draft, operation)
      break

    case 'indent_list_item':
      applyIndentListItem(draft, operation)
      break

    case 'unindent_list_item':
      applyUnindentListItem(draft, operation)
      break

    case 'wrap_encrypted':
      applyWrapEncrypted(draft, operation)
      break

    case 'unwrap_encrypted':
      applyUnwrapEncrypted(draft, operation)
      break

    default:
      throw new Error(`Unknown operation type: ${(operation as any).type}`)
  }
}

/**
 * Applies insert text operation
 */
const applyInsertText = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block) {
    throw new Error(`Block not found: ${operation.blockId}`)
  }

  if (!('content' in block)) {
    throw new Error(`Block ${operation.blockId} does not support text content`)
  }

  const content = block.content as Draft<InlineContent[]>
  insertTextIntoContent(content, operation.offset, operation.text, operation.marks)
}

/**
 * Applies delete text operation
 */
const applyDeleteText = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block) {
    throw new Error(`Block not found: ${operation.blockId}`)
  }

  if (!('content' in block)) {
    throw new Error(`Block ${operation.blockId} does not support text content`)
  }

  const content = block.content as Draft<InlineContent[]>
  deleteTextFromContent(content, operation.offset, operation.length)
}

/**
 * Applies insert block operation
 */
const applyInsertBlock = (draft: Draft<ContentAST>, operation: any): void => {
  draft.blocks.splice(operation.index, 0, operation.block as Draft<Block>)
}

/**
 * Applies delete block operation
 */
const applyDeleteBlock = (draft: Draft<ContentAST>, operation: any): void => {
  if (operation.index >= 0 && operation.index < draft.blocks.length) {
    draft.blocks.splice(operation.index, 1)
  }
}

/**
 * Applies split block operation (with Google Docs direction inheritance)
 */
const applySplitBlock = (draft: Draft<ContentAST>, operation: any): void => {
  const blockIndex = draft.blocks.findIndex((b) => b.id === operation.blockId)
  if (blockIndex === -1) {
    throw new Error(`Block not found: ${operation.blockId}`)
  }

  const block = draft.blocks[blockIndex]
  if (!('content' in block)) {
    throw new Error(`Block ${operation.blockId} cannot be split`)
  }

  const content = block.content as Draft<InlineContent[]>
  const { beforeContent, afterContent } = splitContentAtOffset(content, operation.offset)

  // Update original block with content before split
  ;(block as any).content = beforeContent

  // Create new block with content after split
  const newBlock = {
    ...block,
    id: operation.newBlockId,
    content: afterContent,
  }

  // Google Docs behavior: New block inherits direction from the split block
  // If the original block has an explicit direction, the new block inherits it
  // This ensures direction continuation when pressing Enter
  if (block.attrs && 'dir' in block.attrs && block.attrs.dir) {
    newBlock.attrs = {
      ...newBlock.attrs,
      dir: block.attrs.dir,
    }
  }

  // Insert new block after current one
  draft.blocks.splice(blockIndex + 1, 0, newBlock as Draft<Block>)
}

/**
 * Applies merge blocks operation
 */
const applyMergeBlocks = (draft: Draft<ContentAST>, operation: any): void => {
  const sourceBlockIndex = draft.blocks.findIndex((b) => b.id === operation.blockId)
  const targetBlockIndex = draft.blocks.findIndex((b) => b.id === operation.targetBlockId)

  if (sourceBlockIndex === -1 || targetBlockIndex === -1) {
    throw new Error('Source or target block not found for merge')
  }

  const sourceBlock = draft.blocks[sourceBlockIndex]
  const targetBlock = draft.blocks[targetBlockIndex]

  if (!('content' in sourceBlock) || !('content' in targetBlock)) {
    throw new Error('Both blocks must support content for merge')
  }

  // Merge content from source into target
  const targetContent = targetBlock.content as Draft<InlineContent[]>
  const sourceContent = sourceBlock.content as Draft<InlineContent[]>

  targetContent.push(...sourceContent)

  // Remove source block
  draft.blocks.splice(sourceBlockIndex, 1)
}

/**
 * Applies set mark operation
 */
const applySetMark = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block || !('content' in block)) {
    throw new Error(`Block not found or does not support content: ${operation.blockId}`)
  }

  const content = block.content as Draft<InlineContent[]>
  applyMarkToRange(content, operation.offset, operation.length, operation.mark, 'add')
}

/**
 * Applies remove mark operation
 */
const applyRemoveMark = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block || !('content' in block)) {
    throw new Error(`Block not found or does not support content: ${operation.blockId}`)
  }

  const content = block.content as Draft<InlineContent[]>
  applyMarkToRange(content, operation.offset, operation.length, operation.mark, 'remove')
}

/**
 * Applies set block attributes operation
 */
const applySetBlockAttrs = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block) {
    throw new Error(`Block not found: ${operation.blockId}`)
  }

  ;(block as any).attrs = operation.attrs
}

/**
 * Applies move block operation
 */
const applyMoveBlock = (draft: Draft<ContentAST>, operation: any): void => {
  const { fromIndex, toIndex } = operation

  if (fromIndex < 0 || fromIndex >= draft.blocks.length || toIndex < 0 || toIndex >= draft.blocks.length) {
    throw new Error('Invalid block indices for move operation')
  }

  const [movedBlock] = draft.blocks.splice(fromIndex, 1)
  draft.blocks.splice(toIndex, 0, movedBlock)
}

/**
 * Helper: Finds a block in draft AST
 */
const findBlockInDraft = (draft: Draft<ContentAST>, blockId: string): Draft<Block> | null => {
  return draft.blocks.find((block) => block.id === blockId) || null
}

/**
 * Helper: Inserts text into inline content at offset
 */
const insertTextIntoContent = (content: Draft<InlineContent[]>, offset: number, text: string, marks?: any[]): void => {
  let currentOffset = 0

  for (let i = 0; i < content.length; i++) {
    const node = content[i]

    if (node.type === 'text') {
      const nodeLength = node.text.length

      if (currentOffset + nodeLength >= offset) {
        // Insert within this text node
        const localOffset = offset - currentOffset
        const beforeText = node.text.slice(0, localOffset)
        const afterText = node.text.slice(localOffset)

        // Update current node with text before insertion
        ;(node as any).text = beforeText

        // Create new text node with inserted text
        const newNode = {
          type: 'text' as const,
          text: text,
          marks: marks || node.marks,
        }

        // Create text node with remaining text if any
        if (afterText) {
          const afterNode = {
            type: 'text' as const,
            text: afterText,
            marks: node.marks,
          }
          content.splice(i + 1, 0, newNode as Draft<InlineContent>, afterNode as Draft<InlineContent>)
        } else {
          content.splice(i + 1, 0, newNode as Draft<InlineContent>)
        }

        return
      }

      currentOffset += nodeLength
    } else if (node.type === 'encrypted') {
      // For encrypted nodes, we don't insert text directly
      const nodeText = getEncryptedNodeText(node)
      currentOffset += nodeText.length
    }
  }

  // If we get here, insert at the end
  const newNode = {
    type: 'text' as const,
    text: text,
    marks: marks,
  }
  content.push(newNode as Draft<InlineContent>)
}

/**
 * Helper: Deletes text from inline content
 */
const deleteTextFromContent = (content: Draft<InlineContent[]>, offset: number, length: number): void => {
  let currentOffset = 0
  let remainingLength = length

  for (let i = content.length - 1; i >= 0 && remainingLength > 0; i--) {
    const node = content[i]

    if (node.type === 'text') {
      const nodeLength = node.text.length

      if (currentOffset + nodeLength > offset) {
        // Delete from this text node
        const deleteStart = Math.max(0, offset - currentOffset)
        const deleteEnd = Math.min(nodeLength, deleteStart + remainingLength)
        const deletedLength = deleteEnd - deleteStart

        const beforeText = node.text.slice(0, deleteStart)
        const afterText = node.text.slice(deleteEnd)

        if (beforeText || afterText) {
          ;(node as any).text = beforeText + afterText
        } else {
          // Remove empty text node
          content.splice(i, 1)
        }

        remainingLength -= deletedLength
      }

      currentOffset += nodeLength
    }
  }
}

/**
 * Helper: Splits content at offset
 */
const splitContentAtOffset = (
  content: Draft<InlineContent[]>,
  offset: number,
): { beforeContent: Draft<InlineContent[]>; afterContent: Draft<InlineContent[]> } => {
  const beforeContent: Draft<InlineContent[]> = []
  const afterContent: Draft<InlineContent[]> = []

  let currentOffset = 0

  for (const node of content) {
    if (node.type === 'text') {
      const nodeLength = node.text.length

      if (currentOffset + nodeLength <= offset) {
        // Entire node goes to before
        beforeContent.push(node)
      } else if (currentOffset >= offset) {
        // Entire node goes to after
        afterContent.push(node)
      } else {
        // Split this text node
        const localOffset = offset - currentOffset
        const beforeText = node.text.slice(0, localOffset)
        const afterText = node.text.slice(localOffset)

        if (beforeText) {
          beforeContent.push({
            ...node,
            text: beforeText,
          } as Draft<InlineContent>)
        }

        if (afterText) {
          afterContent.push({
            ...node,
            text: afterText,
          } as Draft<InlineContent>)
        }
      }

      currentOffset += nodeLength
    } else {
      // For non-text nodes, put in appropriate section
      const nodeText = getEncryptedNodeText(node)
      if (currentOffset < offset) {
        beforeContent.push(node)
      } else {
        afterContent.push(node)
      }
      currentOffset += nodeText.length
    }
  }

  return { beforeContent, afterContent }
}

/**
 * Helper: Applies or removes marks from a range of content
 */
const applyMarkToRange = (
  content: Draft<InlineContent[]>,
  offset: number,
  length: number,
  mark: any,
  action: 'add' | 'remove',
): void => {
  let currentOffset = 0

  for (const node of content) {
    if (node.type === 'text') {
      const nodeLength = node.text.length
      const rangeStart = Math.max(0, offset - currentOffset)
      const rangeEnd = Math.min(nodeLength, offset + length - currentOffset)

      if (rangeStart < rangeEnd) {
        // This node intersects with the range
        const marks = (node.marks as any[]) || []

        if (action === 'add') {
          // Add mark if not already present
          const markExists = marks.some((m) => JSON.stringify(m) === JSON.stringify(mark))
          if (!markExists) {
            ;(node as any).marks = [...marks, mark]
          }
        } else {
          // Remove mark
          ;(node as any).marks = marks.filter((m) => JSON.stringify(m) !== JSON.stringify(mark))
        }
      }

      currentOffset += nodeLength
    } else if (node.type === 'encrypted') {
      const nodeText = getEncryptedNodeText(node)
      currentOffset += nodeText.length
    }
  }
}

/**
 * Helper: Gets text content from encrypted node
 */
const getEncryptedNodeText = (node: any): string => {
  if (node.content && Array.isArray(node.content)) {
    return node.content.map((n: any) => (n.type === 'text' ? n.text : '')).join('')
  }
  return '[ENCRYPTED]'
}

/**
 * Applies indent list item operation
 */
const applyIndentListItem = (draft: Draft<ContentAST>, operation: any): void => {
  const listBlock = draft.blocks.find((block) => block.id === operation.parentListId)
  if (!listBlock || listBlock.type !== 'list') {
    throw new Error(`List block not found: ${operation.parentListId}`)
  }

  const listItems = (listBlock as any).items
  const itemToIndent = findItemInList(listItems, operation.itemId)

  if (!itemToIndent) {
    throw new Error(`List item not found: ${operation.itemId}`)
  }

  // Find the item in the root level and get its index
  const itemIndex = listItems.findIndex((item: any) => item.id === operation.itemId)
  if (itemIndex === -1) {
    throw new Error(`Item not found at root level: ${operation.itemId}`)
  }

  if (itemIndex === 0) {
    throw new Error('Cannot indent first item in list')
  }

  // Remove item from current position
  const [item] = listItems.splice(itemIndex, 1)

  // Add to previous sibling's children
  const previousSibling = listItems[itemIndex - 1]
  if (!previousSibling.children) {
    previousSibling.children = []
  }
  previousSibling.children.push(item)
}

/**
 * Applies unindent list item operation
 */
const applyUnindentListItem = (draft: Draft<ContentAST>, operation: any): void => {
  const listBlock = draft.blocks.find((block) => block.id === operation.parentListId)
  if (!listBlock || listBlock.type !== 'list') {
    throw new Error(`List block not found: ${operation.parentListId}`)
  }

  const listItems = (listBlock as any).items
  const result = findItemWithParent(listItems, operation.itemId)

  if (!result) {
    throw new Error(`Cannot find item to unindent: ${operation.itemId}`)
  }

  if (!result.parent) {
    throw new Error('Cannot unindent item at root level')
  }

  // Remove item from parent's children
  const parentIndex = result.parent.children.findIndex((item: any) => item.id === operation.itemId)
  if (parentIndex === -1) {
    throw new Error(`Item not found in parent's children: ${operation.itemId}`)
  }

  const [item] = result.parent.children.splice(parentIndex, 1)

  // Add to parent's level (after the parent)
  const parentInRootIndex = listItems.findIndex((rootItem: any) => rootItem.id === result.parent.id)
  if (parentInRootIndex !== -1) {
    listItems.splice(parentInRootIndex + 1, 0, item)
  } else {
    // If parent is nested, find its position in the hierarchy
    listItems.push(item) // Fallback to end of list
  }
}

/**
 * Helper: Finds an item recursively in a list
 */
const findItemInList = (items: any[], itemId: string): any => {
  for (const item of items) {
    if (item.id === itemId) {
      return item
    }
    if (item.children && item.children.length > 0) {
      const found = findItemInList(item.children, itemId)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Helper: Finds an item with its parent information
 */
const findItemWithParent = (items: any[], itemId: string, parent?: any): { item: any; parent: any } | null => {
  for (const item of items) {
    if (item.id === itemId) {
      return { item, parent }
    }
    if (item.children && item.children.length > 0) {
      const result = findItemWithParent(item.children, itemId, item)
      if (result) {
        return result
      }
    }
  }
  return null
}

/**
 * Applies wrap encrypted operation
 * Wraps selected text into an EncryptedNode
 */
const applyWrapEncrypted = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block || !('content' in block)) {
    throw new Error(`Block not found or doesn't support content: ${operation.blockId}`)
  }

  const content = block.content as Draft<any[]>
  const { offset, length, encryptionId } = operation

  // Extract the text nodes that should be wrapped
  const extractedContent: Draft<any>[] = []
  let currentOffset = 0
  let startIndex = -1
  let endIndex = -1
  let startNodeOffset = 0

  // Find the range of nodes to extract
  for (let i = 0; i < content.length; i++) {
    const node = content[i]

    if (node.type === 'text') {
      const nodeLength = node.text.length

      // Check if this node contains the start of selection
      if (startIndex === -1 && currentOffset + nodeLength > offset) {
        startIndex = i
        startNodeOffset = offset - currentOffset
      }

      // Check if this node contains the end of selection
      if (startIndex !== -1 && currentOffset + nodeLength >= offset + length) {
        endIndex = i
        break
      }

      currentOffset += nodeLength
    } else if (node.type === 'encrypted') {
      // Calculate length of encrypted node
      const encryptedLength = node.content.reduce(
        (sum: number, n: any) => sum + (n.type === 'text' ? n.text.length : 0),
        0,
      )
      currentOffset += encryptedLength
    } else if (node.type === 'link') {
      // Calculate length of link node
      const linkLength = node.content.reduce((sum: number, n: any) => sum + (n.type === 'text' ? n.text.length : 0), 0)
      currentOffset += linkLength
    }
  }

  if (startIndex === -1 || endIndex === -1) {
    throw new Error('Cannot find text range to encrypt')
  }

  // Extract and wrap the content
  currentOffset = 0
  for (let i = 0; i <= endIndex; i++) {
    const node = content[i]

    if (i < startIndex) {
      if (node.type === 'text') {
        currentOffset += node.text.length
      }
      continue
    }

    if (node.type === 'text') {
      const nodeLength = node.text.length
      const nodeStart = currentOffset
      // const nodeEnd = currentOffset + nodeLength // not used

      if (i === startIndex && i === endIndex) {
        // Selection is within a single node
        const beforeText = node.text.slice(0, startNodeOffset)
        const selectedText = node.text.slice(startNodeOffset, startNodeOffset + length)
        const afterText = node.text.slice(startNodeOffset + length)

        // Replace the node with: before + encrypted + after
        const newContent: any[] = []
        if (beforeText) {
          const beforeNode: any = { type: 'text', text: beforeText }
          if (node.marks) {
            beforeNode.marks = node.marks
          }
          newContent.push(beforeNode)
        }

        const encryptedTextNode: any = { type: 'text', text: selectedText }
        if (node.marks) {
          encryptedTextNode.marks = node.marks
        }

        newContent.push({
          type: 'encrypted',
          id: encryptionId,
          algo: 'aes-gcm',
          version: 1,
          content: [encryptedTextNode],
        })

        if (afterText) {
          const afterNode: any = { type: 'text', text: afterText }
          if (node.marks) {
            afterNode.marks = node.marks
          }
          newContent.push(afterNode)
        }

        // Remove old node and insert new ones
        content.splice(i, 1, ...(newContent as any))

        return
      } else if (i === startIndex) {
        // First node in selection
        const selectedText = node.text.slice(startNodeOffset)
        if (startNodeOffset > 0) {
          node.text = node.text.slice(0, startNodeOffset)
        } else {
          content.splice(i, 1)
          endIndex--
          i--
        }
        extractedContent.push({ type: 'text', text: selectedText, marks: node.marks })
      } else if (i === endIndex) {
        // Last node in selection
        const endOffset = offset + length - nodeStart
        const selectedText = node.text.slice(0, endOffset)
        extractedContent.push({ type: 'text', text: selectedText, marks: node.marks })
        node.text = node.text.slice(endOffset)
        if (node.text === '') {
          content.splice(i, 1)
        }
      } else {
        // Middle node - include entirely
        extractedContent.push(node)
        content.splice(i, 1)
        endIndex--
        i--
      }

      currentOffset += nodeLength
    }
  }

  // Create encrypted node and insert it
  const encryptedNode = {
    type: 'encrypted',
    id: encryptionId,
    algo: 'aes-gcm',
    version: 1,
    content: extractedContent,
  }

  content.splice(startIndex, 0, encryptedNode)
}

/**
 * Applies unwrap encrypted operation
 * Unwraps an EncryptedNode back to its content
 */
const applyUnwrapEncrypted = (draft: Draft<ContentAST>, operation: any): void => {
  const block = findBlockInDraft(draft, operation.blockId)
  if (!block || !('content' in block)) {
    throw new Error(`Block not found or doesn't support content: ${operation.blockId}`)
  }

  const content = block.content as Draft<any[]>
  const { encryptionId } = operation

  // Find the encrypted node with the matching ID
  for (let i = 0; i < content.length; i++) {
    const node = content[i]

    if (node.type === 'encrypted' && node.id === encryptionId) {
      // Get the full text of the encrypted node
      const fullText = node.content
        .filter((n: any) => n.type === 'text')
        .map((n: any) => n.text)
        .join('')

      // Check if the text contains paragraph breaks (\n\n).
      // Multi-paragraph encryption merges paragraphs with \n\n separators.
      // On decrypt, restore the original paragraph structure.
      if (fullText.includes('\n\n') && block.type === 'paragraph') {
        const paragraphs = fullText.split('\n\n')
        const blockIndex = draft.blocks.indexOf(block as any)
        if (blockIndex === -1) {
          // Fallback: just unwrap inline
          content.splice(i, 1, ...node.content)
          return
        }

        // Replace encrypted node with first paragraph's text in the current block
        const firstText = paragraphs[0]
        const beforeEncrypted = content.slice(0, i)
        const afterEncrypted = content.slice(i + 1)
        block.content = [
          ...beforeEncrypted,
          ...(firstText ? [createTextNode(firstText) as any] : []),
          ...afterEncrypted,
        ] as any

        // Insert new paragraph blocks for remaining paragraphs
        const newBlocks: any[] = []
        for (let p = 1; p < paragraphs.length; p++) {
          const para = createParagraph(paragraphs[p], (block as any).attrs)
          newBlocks.push(para)
        }
        draft.blocks.splice(blockIndex + 1, 0, ...newBlocks)
      } else {
        // Simple case: no paragraph breaks, just unwrap inline
        content.splice(i, 1, ...node.content)
      }
      return
    }
  }

  throw new Error(`Encrypted node not found: ${encryptionId}`)
}
